import React, { useState, useRef } from 'react';
import { ImageIcon, Upload, FolderOpen } from 'lucide-react';

/**
 * Image block — displays image with resize handles, supports content library + upload.
 */
export default function EmailImageBlock({ props, onChange, isSelected, onOpenContentPicker }) {
  const [dragging, setDragging] = useState(false);
  const startRef = useRef(null);

  const handleResize = (e) => {
    if (!startRef.current) return;
    const diff = e.clientX - startRef.current.x;
    const newWidth = Math.max(50, startRef.current.width + diff);
    onChange({ ...props, width: `${newWidth}px` });
  };

  const handleResizeEnd = () => {
    setDragging(false);
    startRef.current = null;
    window.removeEventListener('mousemove', handleResize);
    window.removeEventListener('mouseup', handleResizeEnd);
  };

  const handleResizeStart = (e) => {
    e.stopPropagation();
    const img = e.target.closest('[data-image-block]')?.querySelector('img');
    if (!img) return;
    startRef.current = { x: e.clientX, width: img.offsetWidth };
    setDragging(true);
    window.addEventListener('mousemove', handleResize);
    window.addEventListener('mouseup', handleResizeEnd);
  };

  if (!props.image_url) {
    return (
      <div style={{ padding: props.padding || '8px 24px', textAlign: props.alignment || 'center' }}>
        <div className="border-2 border-dashed border-stone-200 rounded-lg p-8 flex flex-col items-center gap-3">
          <ImageIcon className="w-8 h-8 text-stone-300" />
          <p className="text-xs text-stone-400">Add an image</p>
          {isSelected && (
            <div className="flex gap-2">
              <button onClick={onOpenContentPicker}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-stone-900 text-white rounded-lg hover:bg-stone-800">
                <FolderOpen className="w-3 h-3" /> Content Library
              </button>
              <label className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-white border border-stone-200 text-stone-700 rounded-lg hover:bg-stone-50 cursor-pointer">
                <Upload className="w-3 h-3" /> Upload
                <input type="file" accept="image/*" className="hidden" onChange={e => {
                  const file = e.target.files?.[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onload = () => onChange({ ...props, image_url: reader.result, alt_text: file.name });
                    reader.readAsDataURL(file);
                  }
                }} />
              </label>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div data-image-block style={{ padding: props.padding || '8px 24px', textAlign: props.alignment || 'center' }} className="relative group">
      {props.link_url ? (
        <a href={props.link_url} onClick={e => e.preventDefault()}>
          <img src={props.image_url} alt={props.alt_text || ''} style={{ width: props.width || '100%', maxWidth: '100%', height: 'auto', borderRadius: props.border_radius || '0px', display: 'inline-block' }} />
        </a>
      ) : (
        <img src={props.image_url} alt={props.alt_text || ''} style={{ width: props.width || '100%', maxWidth: '100%', height: 'auto', borderRadius: props.border_radius || '0px', display: 'inline-block' }} />
      )}
      {/* Resize handle */}
      {isSelected && (
        <div onMouseDown={handleResizeStart}
          className="absolute right-6 top-1/2 -translate-y-1/2 w-3 h-8 bg-white border border-stone-300 rounded cursor-ew-resize shadow-sm hover:bg-stone-100" />
      )}
      {props.caption && (
        <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#78716c', textAlign: props.alignment || 'center' }}>{props.caption}</p>
      )}
    </div>
  );
}
